import {Grid, useGrid} from '@virtual-grid/react';
import clsx from 'clsx';
import {throttle} from 'lodash';
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  RefObject,
} from 'react';
import InfiniteLoader from '@/components/infinite-loader';
import styles from './VirtualizedGrid.module.scss';

export type VirtualizedGridProps<T> = {
  testID?: string;
  containerClassName?: string;
  data: T[];
  onEndReached?: () => void;
  renderItem: (item: T) => React.ReactNode;
  renderItemSkeleton?: (index: number) => React.ReactNode;
  gap?: {x: number; y: number};
  columns: number;
  itemSize: {width: number; height: number};
  isInitialLoading?: boolean;
  skeletonsAmount?: number;
  showLoader?: boolean;
  gridScrollRef?: RefObject<HTMLDivElement>;
  gridScrollTopKey?: string;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
  tabIndex?: number;
};

function VirtualizedGrid<T>(props: VirtualizedGridProps<T>) {
  const {
    testID = 'virtualized-grid',
    containerClassName,
    data,
    onEndReached,
    renderItem,
    renderItemSkeleton,
    gap,
    columns,
    itemSize,
    isInitialLoading,
    skeletonsAmount,
    showLoader,
    gridScrollRef,
    gridScrollTopKey = 'gridScrollTop',
    onScroll,
    tabIndex,
  } = props;
  const [isInitialized, setIsInitialized] = useState(false);
  const newScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = gridScrollRef || newScrollRef;

  useEffect(() => {
    const scrollTop = sessionStorage.getItem(gridScrollTopKey);
    if (scrollTop) {
      // Hide the view until the scroll is set to prevent visible jump
      const timeout = setTimeout(() => {
        scrollRef.current?.scrollTo(0, Number(scrollTop));
        setIsInitialized(true);
      });
      return () => clearTimeout(timeout);
    }

    setIsInitialized(true);
    return undefined;
  }, []);

  const grid = useGrid({
    scrollRef,
    count:
      data.length +
      (!!isInitialLoading && !!skeletonsAmount && !!renderItemSkeleton
        ? skeletonsAmount
        : 0),

    gap,
    columns,
    size: itemSize,
    onLoadMore: onEndReached,
    overscan: 1,
  });

  const handleOnScroll = useCallback(
    (event?: React.UIEvent<HTMLElement>) => {
      if (event?.target && gridScrollTopKey) {
        const {scrollTop} = event.target as HTMLDivElement;
        sessionStorage.setItem(gridScrollTopKey, scrollTop.toString());
      }
      if (event) {
        onScroll?.(event);
      }
    },
    [gridScrollTopKey, onScroll]
  );

  const handleRenderItem = (index: number) => {
    if (isInitialLoading && renderItemSkeleton) {
      return renderItemSkeleton(index);
    }
    return renderItem(data[index]);
  };

  return (
    <div
      ref={scrollRef}
      className={clsx(
        styles.container,
        containerClassName,
        !isInitialized && styles.notInitialized
      )}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={tabIndex || undefined}
      data-testid={testID}
      onScroll={throttle(handleOnScroll, 100)}
    >
      <Grid grid={grid}>{(index) => handleRenderItem(index)}</Grid>
      <InfiniteLoader
        isLoading={!!showLoader}
        containerClassName={styles.loadingIconContainer}
      />
    </div>
  );
}

export default VirtualizedGrid;
